/**
 * Read-only Seller-Assisted gateway phone review (plan §7 / AC-04 / G14).
 * Merchant + named lines + qty + totals. NO qty/remove/edit mutation controls.
 */
import { useTranslation } from 'react-i18next';
import type { CheckoutSessionDisplayLine } from 'pi-kiosk-shared';
import { formatPrice, toMajorUnits } from '../shop/cart/money.js';

export interface SellerAssistedPhoneReviewProps {
  readonly merchantName?: string | null;
  readonly salesPointName?: string | null;
  readonly lines: readonly CheckoutSessionDisplayLine[];
  readonly amountMinor: number;
  readonly currency: string;
  /** When true, show stale handoff block-pay chrome (G17). */
  readonly staleHandoff?: boolean;
}

export function SellerAssistedPhoneReview({
  merchantName,
  salesPointName,
  lines,
  amountMinor,
  currency,
  staleHandoff = false,
}: SellerAssistedPhoneReviewProps): JSX.Element {
  const { t } = useTranslation('customer');
  const merchant = merchantName?.trim() ?? '';
  const salesPoint = salesPointName?.trim() ?? '';

  return (
    <section
      className="flex flex-col gap-3 rounded-md border border-[var(--color-border)] p-3"
      data-testid="seller-assisted-phone-review"
      aria-label={t('checkout.sellerAssisted.reviewAria')}
    >
      <header className="flex flex-col gap-1">
        <h2 className="text-base font-semibold text-[var(--color-on-surface)]">
          {t('checkout.sellerAssisted.reviewTitle')}
        </h2>
        <p className="text-sm text-[var(--color-on-surface-muted)]">
          {t('checkout.sellerAssisted.reviewSubtitle')}
        </p>
      </header>

      {(merchant.length > 0 || salesPoint.length > 0) ? (
        <dl
          className="flex flex-col gap-1 text-sm"
          data-testid="seller-assisted-phone-review-merchant"
        >
          {merchant.length > 0 ? (
            <div className="flex items-start justify-between gap-3">
              <dt className="text-[var(--color-on-surface-muted)]">
                {t('checkout.sellerAssisted.merchantLabel')}
              </dt>
              <dd className="text-right text-[var(--color-on-surface)]">{merchant}</dd>
            </div>
          ) : null}
          {salesPoint.length > 0 ? (
            <div className="flex items-start justify-between gap-3">
              <dt className="text-[var(--color-on-surface-muted)]">
                {t('checkout.sellerAssisted.salesPointLabel')}
              </dt>
              <dd className="text-right text-[var(--color-on-surface)]">{salesPoint}</dd>
            </div>
          ) : null}
        </dl>
      ) : null}

      <ul
        className="flex flex-col gap-2 border-t border-[var(--color-border)] pt-3"
        data-testid="seller-assisted-phone-review-lines"
      >
        {lines.map((line) => (
          <li
            key={`${line.name}-${line.quantity}-${line.unitPrice}-${line.lineTotal}`}
            className="flex items-start justify-between gap-3 text-sm"
            data-testid="seller-assisted-phone-review-line"
          >
            <div className="min-w-0 flex-1">
              <p className="font-medium text-[var(--color-on-surface)]">{line.name}</p>
              <p className="text-[var(--color-on-surface-muted)]">
                {t('checkout.sellerAssisted.lineQty', { count: line.quantity })}
                {' · '}
                {formatPrice(toMajorUnits(line.unitPrice), currency)}
              </p>
            </div>
            <p className="shrink-0 font-medium text-[var(--color-on-surface)]">
              {formatPrice(toMajorUnits(line.lineTotal), currency)}
            </p>
          </li>
        ))}
      </ul>

      <div
        className="flex items-center justify-between gap-3 border-t border-[var(--color-border)] pt-3"
        data-testid="seller-assisted-phone-review-total"
      >
        <span className="text-sm font-medium text-[var(--color-on-surface)]">
          {t('checkout.sellerAssisted.totalLabel')}
        </span>
        <span className="text-lg font-bold text-[var(--color-on-surface)]">
          {formatPrice(toMajorUnits(amountMinor), currency)}
        </span>
      </div>

      <p
        className="text-xs text-[var(--color-on-surface-muted)]"
        data-testid="seller-assisted-phone-review-ask-staff"
      >
        {t('checkout.sellerAssisted.askStaff')}
      </p>

      <p
        className="text-xs text-[var(--color-on-surface-muted)]"
        data-testid="seller-assisted-phone-review-non-fiscal"
      >
        {t('checkout.sellerAssisted.confirmationNonFiscal')}
      </p>

      {staleHandoff ? (
        <div
          className="rounded-md border border-[var(--color-danger)] bg-[var(--color-danger-muted,transparent)] p-3"
          role="alert"
          data-testid="seller-assisted-stale-handoff"
        >
          <p className="text-sm font-medium text-[var(--color-danger)]">
            {t('checkout.sellerAssisted.staleHandoffTitle')}
          </p>
          <p className="mt-1 text-sm text-[var(--color-on-surface-muted)]">
            {t('checkout.sellerAssisted.staleHandoffRecovery')}
          </p>
        </div>
      ) : null}
    </section>
  );
}
